import { TrafficReportProps } from "./IConfiguredTrafficReport";
import { AggregatedTargetAttribute, AggregationPeriod, LocationUsageGrouping, Reports, rawPost, TrafficReportConfiguration, useAuth } from "@app/shared";
import { useContext, useEffect, useMemo } from "react";
import { colorMap, getGroupingDisplayName, ReportContext } from "./reportHelpers";
import { useFetch } from "../../hooks";
import { ReportLoadingErrorWrapper } from "./ReportLoadingErrorWrapper";
import { Bar, ChartProps } from 'react-chartjs-2';
import { Chart, registerables, TooltipItem } from 'chart.js';

Chart.register(...registerables);

export const downloadFacilityUsageCSV = async (aggLevel: AggregationPeriod, opts: TrafficReportConfiguration) => {
    try {
        const response = await rawPost(`/api/report/facility-usage/by-${aggLevel}?csv`,
            JSON.stringify(opts),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/octet-stream'
                }
            });
        const blob = await response.blob();
        if (!response.ok) {
            console.error(response.statusText);
            return;
        }

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);

        const contentDispositionHeader = response.headers.get('content-disposition');
        const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = fileNameRegex.exec(contentDispositionHeader || '');
        const fileName = matches && matches[1] ? matches[1] : 'LocationUtilizationReport.csv';

        link.download = fileName;
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error('Download failed:', error);
    }
};

export function GetFacilityUsageDataLoader(aggLevel: AggregationPeriod, durationFormatter: (timezone: string, periodStart: Date) => string): React.FC<TrafficReportProps> {
    return function MYFC(props: TrafficReportProps) {
        const { searchParams, reportConfig, setIsLoading } = useContext(ReportContext);
        const { numberFormatter } = useAuth();
        const { grouping, chartTimezone } = reportConfig;
        const { size } = props;
        const [data, error, { isLoading }] = useFetch(() => Reports.facilityScreenLineTotals(aggLevel, searchParams), [aggLevel, searchParams]);
        useEffect(() => setIsLoading(isLoading), [isLoading, setIsLoading]);
        const sortedData = useMemo(() => {
            if (!data?.length) { return [] }
            return [...data].sort((a, b) => a.periodStart!.getTime() - b.periodStart!.getTime())
        }, [data]);

        const xValues = useMemo(() => Array.from(new Set(sortedData.map(item => item.periodStart!.getTime()) ?? [])).map(x => new Date(x)), [sortedData]);

        const datasets = useMemo(() => {
            if (grouping === AggregatedTargetAttribute.None) {
                return [{
                    label: 'Inbound',
                    data: sortedData.map(x => x.inCount),
                    backgroundColor: colorMap[grouping][0],
                    stack: 'Inbound'
                }, {
                    label: 'Outbound',
                    data: sortedData.map(x => x.outCount),
                    backgroundColor: colorMap[grouping][0],
                    stack: 'Outbound'
                }];
            }
            const stacked = Array.from(sortedData.reduce<Map<number, LocationUsageGrouping[]>>((acc, item) => {
                if (!acc.has(item.aggregatedValue!)) {
                    acc.set(item.aggregatedValue!, []);
                }
                acc.get(item.aggregatedValue!)!.push(item);
                return acc;
            }, new Map()).values());
            return stacked.flatMap((s, idx) => ([{
                label: getGroupingDisplayName(grouping, s[0].aggregatedValue!),
                data: s.map(x => x.inCount),
                backgroundColor: colorMap[grouping][s[0].aggregatedValue!],
                stack: 'Inbound'
            }, {
                label: getGroupingDisplayName(grouping, s[0].aggregatedValue!),
                data: s.map(x => x.outCount),
                backgroundColor: colorMap[grouping][s[0].aggregatedValue!],
                stack: 'Outbound'
            }]));
        }, [sortedData, grouping]);

        const labels = useMemo(() => xValues.map(x => durationFormatter(chartTimezone, x)), [xValues, chartTimezone, durationFormatter]);

        const chartjsProps = {
            data: {
                labels,
                datasets
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: grouping !== AggregatedTargetAttribute.None,
                        labels: {
                            filter: (legendItem, chartData) => {
                                const datasetIndex = legendItem.datasetIndex;
                                if (datasetIndex === undefined || !chartData?.datasets) return false;

                                // Check if the dataset has any non-zero values
                                return chartData.datasets[datasetIndex].data.some(value => value !== 0);
                            },
                            generateLabels: (chart) => {
                                const datasets = chart.data.datasets;
                                const uniqueLabels: Array<{ text: string | undefined, fillStyle: any }> = [];

                                // Create unique entries for each color
                                datasets.forEach((dataset) => {
                                    const existing = uniqueLabels.find(
                                        (entry) => entry.text === dataset.label && entry.fillStyle === dataset.backgroundColor
                                    );
                                    if (!existing) {
                                        uniqueLabels.push({
                                            text: dataset.label,
                                            fillStyle: dataset.backgroundColor,
                                        });
                                    }
                                });

                                return uniqueLabels;
                            },
                        }
                    },
                    tooltip: {
                        filter: (tooltipItem: TooltipItem<'bar'>) => {
                            return !Number.isNaN(tooltipItem.raw) && tooltipItem.raw !== 0; // Exclude zero values
                        },
                        callbacks: {
                            footer: (tooltipItems: TooltipItem<'bar'>[]) => {
                                const inTotal = tooltipItems.reduce((sum, tooltipItem) => {
                                    return sum + (tooltipItem.dataset.stack === "Inbound" ? tooltipItem.raw as number : 0);
                                }, 0);
                                const outTotal = tooltipItems.reduce((sum, tooltipItem) => {
                                    return sum + (tooltipItem.dataset.stack === "Outbound" ? tooltipItem.raw as number : 0);
                                }, 0);
                                return [`Inbound:  ${numberFormatter.format(inTotal)}`, `Outbound: ${numberFormatter.format(outTotal)}`];
                            },
                            label: (context) => {
                                const dataset = context.dataset;

                                return dataset.stack === 'Inbound' ? `${dataset.label} In ${context.raw}`
                                    : [`${dataset.label} Out: ${context.raw}`, ''];
                            },
                        },
                    },
                },
                interaction: {
                    mode: 'index', // Ensures all datasets for the same label are shown
                    intersect: false, // Ensures tooltips show even if cursor isn't directly over a bar
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        stacked: true
                    },
                    x: {
                        stacked: true
                    }
                }
            }
        } as Omit<ChartProps<"bar", (number | [number, number] | null)[], unknown>, "type">;

        return <ReportLoadingErrorWrapper error={error} hasNoData={!!!xValues?.length} isLoading={isLoading} size={props.size}>
            <div style={{ position: 'relative', width: props.size.width, height: size.height ?? 1100 }}>
                <Bar  {...chartjsProps as ChartProps<'bar'>} ></Bar>
            </div>
        </ReportLoadingErrorWrapper>

    };
}